import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { finalize, lastValueFrom, map, Observable, shareReplay, Subject, tap } from 'rxjs';
import { LoginResponse, SignupRequest, SignupResponse } from '@uranda/auth';
import { ApiService, GenericResponse } from '@uranda/commons';
import { Router } from '@angular/router';
import { ProfileService } from './services/profile.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiService {

  accessTokenKey = 'accessTokenKey';
  refreshTokenKey = 'refreshTokenKey';

  private logoutEvent = new Subject<string>();
  private refresh$?: Observable<string>;
  router?: Router;
  profileService = this.injector.get(ProfileService);


  async login(username?: string, password?: string): Promise<LoginResponse> {
    const response = await lastValueFrom(
      this.httpClient.post<any>(
        this.getEndpoint('/v1/auth/token'),
        { username: username, password: password }
      )
    );
    this.setToken(response.access_token, response.refresh_token);
    return response;
  }

  refreshToken(): Observable<string> {
    // Single-flight: se un refresh è già in corso, riusa lo stesso Observable
    // così N richieste parallele scatenano una sola POST /refresh-token.
    if (this.refresh$) {
      return this.refresh$;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.getRefreshToken()}`
    });

    this.refresh$ = this.httpClient.post<{ access_token: string, refresh_token: string }>(
      this.getEndpoint('/v1/auth/refresh-token'),
      {},
      { headers }
    ).pipe(
      tap((response) => {
        this.setToken(response.access_token, response.refresh_token);
      }),
      map((response) => response.access_token),
      shareReplay(1),
      finalize(() => {
        this.refresh$ = undefined;
      })
    );

    return this.refresh$;
  }

  isTokenExpired(): boolean {
    return this.isJwtExpired(this.getToken());
  }

  /** Decodifica un JWT e dice se è scaduto (o assente / non decodificabile). */
  private isJwtExpired(token: string | null): boolean {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }

  isUserLoggedIn() {
    return this.getToken() !== null;
  }

  /**
   * Sessione utilizzabile da guard/bootstrap: o l'access token è ancora valido,
   * oppure è scaduto/assente ma esiste un refresh token ancora valido con cui
   * rinnovarlo (l'interceptor lo farà alla prima chiamata). A differenza di
   * isUserLoggedIn(), NON si accontenta della sola presenza del token: così non
   * mostriamo una dashboard "stantia" per una sessione ormai morta, scoprendolo
   * solo al primo click. isUserLoggedIn() resta basato sulla presenza perché
   * l'interceptor lo usa per decidere se rinnovare proattivamente un token scaduto.
   */
  canRecoverSession(): boolean {
    if (this.getToken() && !this.isTokenExpired()) {
      return true;
    }
    return this.isRefreshTokenValid();
  }

  /**
   * true solo se esiste un refresh token NON scaduto: è l'unico caso in cui ha senso chiamare
   * /v1/auth/refresh-token. Con un refresh token scaduto (7gg) la POST darebbe solo 401.
   */
  isRefreshTokenValid(): boolean {
    const refreshToken = this.getRefreshToken();
    return refreshToken != null && !this.isJwtExpired(refreshToken);
  }

  /**
   * Pulisce in silenzio la sessione locale (token + profilo) SENZA redirect al login:
   * usato fuori dalle aree protette quando la sessione è morta e non recuperabile.
   */
  clearSession(): void {
    this.clearToken();
    this.profileService.clearProfile();
  }

  setToken(access_token: string, refresh_token?: string) {
    localStorage.setItem(this.accessTokenKey, access_token);
    if (refresh_token) {
      localStorage.setItem(this.refreshTokenKey, refresh_token);
    }
  }

  getToken() {
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  clearToken() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    this.refresh$ = undefined;
  }

  isTokenNearExpiry(thresholdInSeconds = 300): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      // true se già scaduto o entro la soglia → va rinnovato prima dell'uso
      return payload.exp - currentTime < thresholdInSeconds;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }

  logout() {
    this.clearToken();
    this.profileService.clearProfile();
    this.logoutEvent.next('');
    window.location.href = '/login';
  }

  logoutListener() {
    return this.logoutEvent.asObservable();
  }

  async requestResetPassword(email: string) {
    let queryParameters = new HttpParams().set('email', email)
    const response = await this.post('/v1/auth/request-reset-password', queryParameters);
    return response.data;
  }

  async verifyResetPassword(token: string): Promise<{ firstname: string, lastname: string, email: string }> {
    const body = { token: token };
    const response = await this.post('/v1/auth/verify-reset-password', body);
    return response.data;
  }

  async resetPassword(request: { token: string, password: string, passwordConfirm: string }) {
    const response = await this.post('/v1/auth/reset-password', request);
    return response.data;
  }
}
